import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import ApartmentIcon from '@mui/icons-material/Apartment'
import BoltIcon from '@mui/icons-material/Bolt'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import CheckIcon from '@mui/icons-material/Check'
import type { StepIndex } from './schema'

type RailCopy = {
  icon: React.ElementType
  title: string
  desc: string
  bullets: string[]
}

const RAIL_COPY: Record<StepIndex, RailCopy> = {
  0: {
    icon: ApartmentIcon,
    title: 'Verify Your Business',
    desc: 'Tell us about your operation so we can match you with the right pros, fast.',
    bullets: [
      'Instant Verification',
      'Location Approval in Minutes',
      'Business Profile Creation',
    ],
  },
  1: {
    icon: BoltIcon,
    title: 'Lock In Your Shift',
    desc: 'Set the date and time. We alert top-rated pros in your area immediately.',
    bullets: [
      'Hyper-Local Matching',
      '4.5+ Star Rated Pros Only',
      'Real-Time Shift Alerts',
    ],
  },
  2: {
    icon: VerifiedUserIcon,
    title: 'Secure Your Roster',
    desc: 'Authorize payment now. You only get charged after the shift is complete.',
    bullets: [
      'Secure Booking Capture',
      'Liability Insurance Included',
      'Zero Post-Shift Invoices',
    ],
  },
}

type Props = { step: StepIndex }

export const SideRail: React.FC<Props> = ({ step }) => {
  const rail = RAIL_COPY[step]
  const Icon = rail.icon
  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 228 },
        flexShrink: 0,
        backgroundColor: 'common.black',
        color: 'common.white',
        padding: '32px 22px',
        display: { xs: 'none', sm: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Box sx={{ color: 'primary.main', mb: 1.5, lineHeight: 0 }}>
          <Icon sx={{ fontSize: 52 }} />
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            fontSize: 15,
            color: 'common.white',
            textTransform: 'uppercase',
            mb: '10px',
          }}
        >
          {rail.title}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: 'neutral.mediumGray',
            lineHeight: 1.6,
            mb: '18px',
          }}
        >
          {rail.desc}
        </Typography>
        <Stack spacing="9px">
          {rail.bullets.map((b) => (
            <Stack
              key={b}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'flex-start' }}
            >
              <CheckIcon
                sx={{
                  fontSize: 16,
                  color: 'primary.main',
                  flexShrink: 0,
                  mt: '2px',
                }}
              />
              <Typography sx={{ fontSize: 12, color: 'common.white' }}>
                {b}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box
        sx={{
          borderTop: (t) => `1px dashed ${t.palette.neutral.gray}`,
          pt: '14px',
          mt: 2,
        }}
      >
        <Typography sx={{ fontSize: 11, color: 'neutral.mediumGray' }}>
          Almost finished
        </Typography>
      </Box>
    </Box>
  )
}
